/**
 * Schedules — the third way an AI Service runs (task 1003).
 *
 * Report: `project/reports/innovate-service-endpoint-schedules.md`.
 *
 * A Schedule is a flat, non-versioned trigger bound to one AI Service: a saved
 * input plus a time rule, either a recurring cron expression read in an IANA
 * timezone or a single instant. It is not part of the collaboration config and
 * is never published, which is why the Schedules surface saves its own rows.
 *
 * These shapes mirror `innovate-service/src/types/domain/schedule.ts` field for
 * field (task 1000), with the timestamps as ISO strings because that is what
 * crosses the wire. Nothing is renamed on the way in — the app reads the
 * service's vocabulary.
 *
 * `ScheduleRun` is the schedule's run log: one row per occurrence the clock
 * produced, whether or not it became a call. It is the ONLY join between a
 * schedule and the runs it caused (kv decision 2026-09-24) — there is no
 * `scheduleId` on `EndpointRequest` — so a row with an `endpointRequestId`
 * links to a run and a row without one is a skip, which is exactly why skips
 * never appear in the AI Service's run history.
 */

/** Recurring on a cron rule, or once at a fixed instant. */
export type ScheduleKind = 'recurring' | 'once';

/**
 * `completed` is a `once` schedule that has fired; `failed` is the worker's
 * automatic pause after repeated refusals or failures, with the reason in
 * `statusDetail` — which the list renders as "Paused automatically", since to
 * the User it is a pause they did not choose. A User may only write `active`
 * or `paused`.
 */
export type ScheduleStatus = 'active' | 'paused' | 'completed' | 'failed';

/** What became of one occurrence the clock produced. */
export type ScheduleRunState =
  | 'fired'
  | 'skipped_overlap'
  | 'skipped_paused'
  | 'rejected'
  | 'completed'
  | 'failed';

export interface Schedule {
  id: string;
  accountId: string;
  /** The AI Service this schedule runs. */
  endpointId: string;
  /**
   * Which release to run, as an id rather than a path (kv decision
   * 2026-09-24 — a path string drifts when a stage is renamed). Null means the
   * bare URL's answer.
   */
  environmentId: string | null;
  name: string;
  description: string | null;
  /** The saved request body, validated against the AI Service's input schema. */
  input: Record<string, unknown>;
  kind: ScheduleKind;
  /** Recurring only: a five-field cron expression. */
  cron: string | null;
  /** Recurring only: the IANA timezone the expression is read in. */
  timezone: string | null;
  /** Once only: the instant to fire. */
  runAt: string | null;
  status: ScheduleStatus;
  /** Why the worker paused it, when it did. */
  statusDetail: string | null;
  consecutiveFailures: number;
  lastRunAt: string | null;
  nextRunAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
}

/** One occurrence in a schedule's run log. History — no soft delete. */
export interface ScheduleRun {
  id: string;
  scheduleId: string;
  /** The instant the occurrence was due, which is not when it ran. */
  scheduledFor: string;
  state: ScheduleRunState;
  /** The request the worker created, when it invoked at all. Null for skips. */
  endpointRequestId: string | null;
  /** The rejection code or the skip reason. */
  detail: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface CreateScheduleInput {
  endpointId: string;
  environmentId?: string;
  name: string;
  description?: string;
  input: Record<string, unknown>;
  kind: ScheduleKind;
  cron?: string;
  timezone?: string;
  runAt?: string;
}

/**
 * A partial edit, plus pause and resume through `status`. The AI Service a
 * schedule belongs to is fixed at creation, as a saved test's is.
 */
export interface UpdateScheduleInput {
  name?: string;
  description?: string;
  environmentId?: string | null;
  input?: Record<string, unknown>;
  kind?: ScheduleKind;
  cron?: string;
  timezone?: string;
  runAt?: string;
  /** Only `active` and `paused`; the other two are the system's words. */
  status?: Extract<ScheduleStatus, 'active' | 'paused'>;
}

/** Which AI Service's schedules, and in which state. */
export interface ScheduleFilters {
  endpointId?: string;
  status?: ScheduleStatus;
  page?: number;
  limit?: number;
}

/**
 * A status as the list renders it. `failed` is deliberately not called
 * "failed" anywhere a User reads: the row says the schedule was paused, and the
 * reason line beside it says why.
 */
export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  failed: 'Paused automatically',
};

/** What each run-log state is called in the occurrences list. */
export const SCHEDULE_RUN_STATE_LABELS: Record<ScheduleRunState, string> = {
  fired: 'Started',
  completed: 'Completed',
  failed: 'Failed',
  rejected: 'Rejected',
  skipped_overlap: 'Skipped — previous run still going',
  skipped_paused: 'Skipped — schedule paused',
};

/**
 * Whether this status occupies one of the plan's `max_schedules` slots.
 *
 * ACTIVE ONLY, because that is what the service counts: task 1000's
 * `countActiveSchedulesForAccount` is `status: 'active', deletedAt: null`. The
 * task text and the AMS seed comment both say paused rows count too; the
 * running code is the authority the User's next create is judged against, so
 * the quota the app shows has to be the number the service will compare to the
 * cap — anything else reads "full" on a section that would accept another one.
 *
 * A completed once-schedule never counts under either reading: it has fired and
 * will not fire again.
 */
export const countsTowardScheduleQuota = (status: ScheduleStatus): boolean =>
  status === 'active';
